import React, { useCallback, useEffect, useState } from 'react';
import { Search, RefreshCw, ChevronLeft, LockKeyhole } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { personal, residence, work, finance, loanFields } from '@/pages/customerDetailsFields';

const labelValue = (field, value) => {
  if (field.boolean) return value ? 'Yes' : 'No';
  if (field.options) {
    const option = field.options.find(item => (Array.isArray(item) ? item[0] : item) === value);
    return option ? (Array.isArray(option) ? option[1] : option) : value;
  }
  return value;
};
const mask = value => value ? `••••${value.slice(-4)}` : '';

const DetailsBlock = ({ title, data, fields, showSensitive }) => {
  if (!data) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">{title}</h3>
      <dl className="grid sm:grid-cols-2 gap-4">
        {fields.filter(f => (f.boolean ? data[f.key] : data[f.key] !== '' && data[f.key] != null) && (!f.when || f.when(data))).map(f => (
          <div key={f.key}>
            <dt className="text-xs text-slate-500">{f.label}</dt>
            <dd className="text-sm text-slate-900 font-medium break-words">
              {!showSensitive && ['pan', 'aadhaar'].includes(f.key) ? mask(data[f.key]) : labelValue(f, data[f.key])}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default function AdminCustomerDetails() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSensitive, setShowSensitive] = useState(false);

  const request = useCallback(async url => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Could not load details. Please sign in again or retry.');
    return res.json();
  }, [token]);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setItems(await request('/api/customer-details')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [request]);
  useEffect(() => { load(); }, [load]);

  const open = async id => {
    setError(''); setLoading(true); setShowSensitive(false);
    try { setSelected(await request(`/api/customer-details/${encodeURIComponent(id)}`)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const filtered = items.filter(item => `${item.name} ${item.id} ${item.mobile}`.toLowerCase().includes(search.toLowerCase()));
  const date = value => value ? new Date(value).toLocaleString('en-IN') : '—';
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <p className="text-xs font-semibold text-[#1e73be] uppercase tracking-widest">Admin only</p>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mt-1">Customer details</h1>
          <p className="text-sm text-slate-600 mt-1">Submitted details from the private customer form.</p>
        </div>
        <button type="button" onClick={load} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"><RefreshCw size={16} /> Refresh</button>
      </div>
      {error && <p role="alert" className="mb-4 text-red-700 bg-red-50 p-3 rounded-lg">{error}</p>}
      {selected ? (
        <>
          <button type="button" onClick={() => { setSelected(null); setShowSensitive(false); }} className="flex items-center gap-2 text-sm text-[#1e73be] mb-4"><ChevronLeft size={16} /> All entries</button>
          <div className="mb-5 rounded-xl bg-blue-50 border border-blue-100 p-4 flex flex-wrap gap-3 items-center justify-between">
            <div><p className="font-bold text-slate-900">{selected.data.applicant.full_name}</p><p className="text-xs text-slate-600">{date(selected.submitted_at)} · ID: {selected.id}</p></div>
            <button type="button" aria-pressed={showSensitive} onClick={() => setShowSensitive(v => !v)} className="text-xs px-3 py-2 rounded-lg border border-blue-200 bg-white flex items-center gap-2"><LockKeyhole size={14} /> {showSensitive ? 'Hide PAN / Aadhaar' : 'Show PAN / Aadhaar'}</button>
          </div>
          <div className="space-y-4">
            {[['Applicant', selected.data.applicant], ['Co-applicant', selected.data.co_applicant]].map(([title, person]) => person && (
              <React.Fragment key={title}>
                <DetailsBlock title={`${title} · Personal`} data={person} fields={personal} showSensitive={showSensitive} />
                <DetailsBlock title={`${title} · Residence`} data={person} fields={residence} showSensitive={showSensitive} />
                <DetailsBlock title={`${title} · Work`} data={person} fields={work} showSensitive={showSensitive} />
                <DetailsBlock title={`${title} · Loans & investments`} data={person} fields={finance} showSensitive={showSensitive} />
              </React.Fragment>
            ))}
            <DetailsBlock title="Loan request" data={selected.data.loan} fields={loanFields} showSensitive={showSensitive} />
            {selected.data.references?.length > 0 && (
              <section className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="font-bold text-lg mb-3">References</h3>
                {selected.data.references.map((r, i) => <p className="text-sm py-2 border-t border-slate-100" key={i}>{r.name || 'No name'} · {r.mobile || 'No mobile'} · {r.address || 'No address'}</p>)}
              </section>
            )}
          </div>
        </>
      ) : (
        <>
          <label className="flex gap-2 items-center bg-white rounded-xl border border-slate-200 px-3 mb-4 max-w-md"><Search size={18} className="text-slate-400" /><span className="sr-only">Search entries</span><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, ID or last 4 digits" className="w-full h-11 outline-none text-sm" /></label>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            {loading ? <p className="p-8 text-slate-500">Loading entries…</p>
              : filtered.length === 0 ? <p className="p-8 text-slate-500">No matching entries yet.</p>
              : <ul className="divide-y divide-slate-100">
                {filtered.map(item => <li key={item.id}>
                  <button type="button" onClick={() => open(item.id)} className="w-full p-4 flex items-center justify-between gap-4 text-left hover:bg-slate-50">
                    <span><span className="block font-semibold text-slate-900">{item.name}</span><span className="text-xs text-slate-500">{item.mobile} · {item.has_co_applicant ? 'With co-applicant' : 'Single applicant'}</span></span>
                    <span className="text-xs text-slate-500 text-right">{date(item.submitted_at)}</span>
                  </button>
                </li>)}
              </ul>}
          </div>
        </>
      )}
    </div>
  );
}